import React from 'react';
import useReport from '../lib/useReport';
import { useFilters } from '../contexts/FilterContext';
import { money, moneyCompact, int, pct, niceDate } from '../lib/format';
import { Card, KpiCard, Spinner, ErrorBox, Empty, DataTable } from '../components/ui';

// SD Intelligence — the suggestion layer (backend docs/sd-intelligence.md).
// v1 surface: churn risk. Unlike the fixed-window Lapsed Regulars report,
// this compares each customer against their OWN ordering rhythm (gap since
// last order > 2× their personal median gap), across every channel.
//
// /intel/status is tier-open and reports the caller's EFFECTIVE access
// (`enabled` = intelligence tier, or a platform super admin viewing any
// tenant); churn-risk is only fetched (and only served) when enabled. The
// nav hides this page on base tier, so the teaser is just a fallback for
// direct navigation. Date filters don't apply here — churn is always "as of
// the latest snapshot"; the location filter does.
export default function IntelligencePage() {
  const { reportParams } = useFilters();
  const locParams = reportParams.locations ? { locations: reportParams.locations } : {};

  const status = useReport('intel/status', {});
  const enabled = !!status.data?.enabled;
  const churn = useReport('intel/churn-risk', locParams, { enabled });

  if (status.loading) return <Spinner label="Checking SD Intelligence…" />;
  if (status.error && !status.data) return <ErrorBox message={status.error} />;
  if (!enabled) return <UpgradeTeaser />;

  return (
    <div className="grid" style={{ gap: 14 }}>
      <ChurnRiskCard state={churn} />
      <RollupNote status={status.data} />
    </div>
  );
}

function UpgradeTeaser() {
  return (
    <Card title="SD Intelligence" sub="turn your reports into recommended actions">
      <div style={{ fontSize: 14, lineHeight: 1.7, maxWidth: 640 }}>
        <p style={{ marginBottom: 10 }}>
          SD Intelligence watches every store's data and surfaces what to do about it:
          regulars going quiet before they're gone, win-back lists ranked by lifetime
          value, and — coming soon — menu pairings worth bundling and discounts that
          cost more than they return.
        </p>
        <p style={{ color: 'var(--ink-2)' }}>
          Not included in your current plan — contact SimplyDine to enable it.
        </p>
      </div>
    </Card>
  );
}

function ChurnRiskCard({ state }) {
  const { data, loading, error } = state;
  return (
    <Card
      tour="intel-churn"
      title="At-Risk Regulars — Act Before They're Gone"
      sub={data?.asOf
        ? `customers overdue vs their own ordering rhythm (2× their usual gap), as of ${niceDate(data.asOf)} · all channels`
        : 'customers whose usual ordering rhythm has been broken'}
    >
      {error ? <ErrorBox message={error} /> :
        loading || !data ? <Spinner label="Comparing customers to their own rhythm…" /> :
        data.churnCount === 0 ? <Empty>No regulars off their rhythm. 🎉</Empty> : (
          <>
            <div className="grid kpi-grid" style={{ marginBottom: 12 }}>
              <KpiCard label="At-Risk Regulars" value={int(data.churnCount)} sub={`${data.criteria.minOrders}+ orders, overdue ${data.criteria.overdueFactor}×, lapsed < ${Math.round(data.criteria.maxLapseDays / 30)} months`} />
              <KpiCard label="Lifetime Value at Risk" value={moneyCompact(data.atRiskValue)} sub="total historical spend of this group" />
              <KpiCard label="Avg Value Each" value={money(Math.round(data.atRiskValue / data.churnCount))} sub="lifetime, per customer" />
              {data.coveragePct != null && (
                <KpiCard label="Customer Coverage" value={pct(data.coveragePct / 100)} sub="of paid orders have a customer attached — walk-in cash orders don't" />
              )}
            </div>
            <DataTable
              csvName="at-risk-regulars.csv"
              defaultSort={{ key: 'lifetimeValue', dir: 'desc' }}
              maxRows={20}
              columns={[
                { key: 'name', label: 'Customer' },
                { key: 'phone', label: 'Phone', render: r => r.phone || '—' },
                { key: 'email', label: 'Email', render: r => r.email || '—' },
                { key: 'orders', label: 'Orders', num: true, render: r => int(r.orders) },
                { key: 'lifetimeValue', label: 'Lifetime Value', num: true, render: r => money(r.lifetimeValue), csv: r => (r.lifetimeValue / 100).toFixed(2) },
                { key: 'medianGapDays', label: 'Usual Gap', num: true, render: r => `${Math.round(r.medianGapDays)}d` },
                { key: 'daysSince', label: 'Quiet For', num: true, render: r => `${int(r.daysSince)}d` },
                { key: 'overdue', label: 'Overdue', num: true, render: r => (r.overdue != null ? `${r.overdue}×` : '—') },
                { key: 'lastOrder', label: 'Last Order', render: r => niceDate(r.lastOrder) },
              ]}
              rows={data.customers.map(c => ({ ...c, __key: c.customerId }))}
            />
            <div className="legend-note">
              Top {data.customers.length} by lifetime value shown; CSV exports the same.
              {data.excludedHouseAccounts > 0 && ` ${int(data.excludedHouseAccounts)} house/staff accounts excluded.`}
              {' '}This is a candidate list, not a send list — before marketing to it, check consent:
              Canadian email needs an express opt-in or a purchase within 2 years (CASL); SMS always
              needs its own express opt-in.
            </div>
          </>
        )}
    </Card>
  );
}

function RollupNote({ status }) {
  const cf = status?.rollups?.customer_facts;
  if (!cf) return null;
  return (
    <div className="legend-note" style={{ paddingLeft: 4 }}>
      Intelligence rollup built from snapshot {cf.sourceDb}
      {cf.info?.customers ? ` · ${int(cf.info.customers)} customers` : ''}
      {cf.builtAt ? ` · ${niceDate(cf.builtAt)}` : ''}
    </div>
  );
}
